import { useState, type ReactNode } from 'react';
import { BodyDefault, MetaDefault } from '@terra/ui-design-system';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import { Tick01Icon } from '@terra/ui-icons';

type TargetSelectorMenuItemRowProps = {
  icon?: ReactNode;
  iconColor?: string;
  label: string;
  description?: string;
  descriptionColor?: string;
  trailing?: ReactNode;
  showCheck?: boolean;
  hoverAction?: ReactNode;
};

export function TargetSelectorMenuItemRow({
  icon,
  iconColor = 'fg.neutral.default',
  label,
  description,
  descriptionColor = 'fg.neutral.subtle',
  trailing,
  showCheck,
  hoverAction,
}: TargetSelectorMenuItemRowProps) {
  const [isHovered, setIsHovered] = useState(false);
  const showHoverAction = Boolean(hoverAction) && isHovered;

  return (
    <HStack
      gap="2"
      w="full"
      alignItems="center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {icon ? (
        <Box
          w="14px"
          h="14px"
          flexShrink={0}
          display="flex"
          alignItems="center"
          color={iconColor}
        >
          {icon}
        </Box>
      ) : null}
      {description ? (
        <VStack gap="0" alignItems="flex-start" minW="0" flex="1">
          <BodyDefault truncate w="full" data-bi-unmask="true">
            {label}
          </BodyDefault>
          <MetaDefault color={descriptionColor}>{description}</MetaDefault>
        </VStack>
      ) : (
        <BodyDefault truncate flex="1" minW="0" data-bi-unmask="true">
          {label}
        </BodyDefault>
      )}
      {trailing}
      <Box
        w="6"
        h="6"
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {showCheck ? (
          <Tick01Icon size={14} color={token('colors.terra.9')} />
        ) : hoverAction ? (
          <Box
            opacity={showHoverAction ? 1 : 0}
            visibility={showHoverAction ? 'visible' : 'hidden'}
            pointerEvents={showHoverAction ? 'auto' : 'none'}
            transition="opacity 0.1s ease, visibility 0.1s ease"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {hoverAction}
          </Box>
        ) : null}
      </Box>
    </HStack>
  );
}
